import { type RuleDto, severityName } from '@vision360/contracts';
import { FlaskConical, MoonStar, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { absoluteTime, SEVERITY_CLASS } from '@/widgets/_sdk/format';
import { useDeleteRule, useRules, useUpdateRule } from './api';
import { BacktestRunner } from './backtest';
import { useConditionSummary } from './condition-summary';
import { RuleDialog } from './rule-dialog';

/**
 * Alert rules (PRD VS-060): the list, enable/disable, edit, backtest, delete.
 * A rule paused by the auto-throttle shows until when (VS-063).
 */

const ACTION = 'h-11';

function RuleRow({
  rule,
  summary,
  now,
  onEdit,
}: {
  rule: RuleDto;
  summary: string;
  now: number;
  onEdit: (rule: RuleDto) => void;
}) {
  const { t } = useTranslation(['rules', 'widgets']);
  const toggle = useUpdateRule();
  const remove = useDeleteRule();
  const [confirming, setConfirming] = useState(false);
  const [testing, setTesting] = useState(false);
  const throttled = rule.throttledUntil && Date.parse(rule.throttledUntil) > now;
  // Show the requested state while the PATCH is in flight.
  const enabled = toggle.isPending
    ? (toggle.variables.patch.enabled ?? rule.enabled)
    : rule.enabled;

  return (
    <tr className="align-top">
      <th scope="row" className="py-3 pr-4 text-left font-medium">
        <span className="block">{rule.name}</span>
        {throttled && (
          <span className="mt-1 inline-block rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
            {t('rules:throttled', {
              time: absoluteTime.format(new Date(rule.throttledUntil ?? 0)),
            })}
          </span>
        )}
      </th>
      <td className="max-w-sm py-3 pr-4 text-sm">{summary}</td>
      <td className="py-3 pr-4">
        <span
          className={cn(
            'rounded-sm px-1.5 py-0.5 text-xs font-medium',
            SEVERITY_CLASS[rule.severity],
          )}
        >
          {t(`widgets:severity.${severityName(rule.severity)}`)}
        </span>
      </td>
      <td className="py-3 pr-4 text-sm">
        <span className="block">
          {rule.channels.map((c) => t(`rules:channels.${c}`)).join(', ')}
        </span>
        {rule.quietHoursOverride && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-sm bg-primary/12 px-1.5 py-0.5 text-xs font-medium text-primary">
            <MoonStar aria-hidden="true" className="h-3.5 w-3.5" />
            {t('rules:quietHoursOverride')}
          </span>
        )}
      </td>
      <td className="py-1 pr-4">
        <label className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-md hover:bg-muted">
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={enabled}
            disabled={toggle.isPending}
            aria-label={t('rules:enableLabel', { name: rule.name })}
            onChange={(e) => toggle.mutate({ id: rule.id, patch: { enabled: e.target.checked } })}
          />
        </label>
      </td>
      <td className="py-1">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className={ACTION}
            aria-label={t('rules:editLabel', { name: rule.name })}
            onClick={() => onEdit(rule)}
          >
            <Pencil aria-hidden="true" className="h-4 w-4" />
            {t('rules:edit')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={ACTION}
            aria-label={t('rules:testLabel', { name: rule.name })}
            onClick={() => setTesting(true)}
          >
            <FlaskConical aria-hidden="true" className="h-4 w-4" />
            {t('rules:test')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={cn(ACTION, 'text-destructive')}
            aria-label={t('rules:deleteLabel', { name: rule.name })}
            onClick={() => setConfirming(true)}
          >
            <Trash2 aria-hidden="true" className="h-4 w-4" />
            {t('rules:delete')}
          </Button>
        </div>
        <div aria-live="polite" className="mt-1 text-xs">
          {toggle.isError && <span className="text-destructive">{t('rules:actionFailed')}</span>}
        </div>

        <Dialog open={testing} onOpenChange={setTesting}>
          <DialogContent closeLabel={t('rules:dialog.close')} className="animate-enter">
            <DialogTitle>{t('rules:backtest.titleRule', { name: rule.name })}</DialogTitle>
            <DialogDescription>{t('rules:backtest.description')}</DialogDescription>
            <BacktestRunner ruleId={rule.id} />
          </DialogContent>
        </Dialog>

        <Dialog open={confirming} onOpenChange={setConfirming}>
          <DialogContent closeLabel={t('rules:confirm.cancel')} className="animate-enter">
            <DialogTitle>{t('rules:confirm.title')}</DialogTitle>
            <DialogDescription>
              {t('rules:confirm.description', { name: rule.name })}
            </DialogDescription>
            {remove.isError && (
              <Alert variant="destructive" role="alert">
                {t('rules:actionFailed')}
              </Alert>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" className={ACTION} onClick={() => setConfirming(false)}>
                {t('rules:confirm.cancel')}
              </Button>
              <Button
                variant="destructive"
                className={ACTION}
                disabled={remove.isPending}
                onClick={() => remove.mutate(rule.id, { onSuccess: () => setConfirming(false) })}
              >
                {remove.isPending && <Spinner />}
                {remove.isPending ? t('rules:confirm.deleting') : t('rules:confirm.delete')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </td>
    </tr>
  );
}

export function RulesPage({ now = Date.now }: { now?: () => number }) {
  const { t } = useTranslation('rules');
  const rules = useRules();
  const summarize = useConditionSummary();
  // `{}` = a new rule; null = no dialog.
  const [editing, setEditing] = useState<{ rule?: RuleDto } | null>(null);
  const hasRules = (rules.data?.rules.length ?? 0) > 0;

  const newButton = (
    <Button className={ACTION} onClick={() => setEditing({})}>
      <Plus aria-hidden="true" className="h-4 w-4" />
      {t('new')}
    </Button>
  );

  let body: React.ReactNode;
  if (rules.isPending) {
    body = (
      <output aria-live="polite" className="flex items-center gap-2 text-muted-foreground">
        <Spinner />
        {t('loading')}
      </output>
    );
  } else if (rules.isError) {
    body = (
      <Alert variant="destructive" role="alert">
        {t('error')}
      </Alert>
    );
  } else if (!hasRules) {
    body = (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-6">
        <p className="text-muted-foreground">{t('empty')}</p>
        {newButton}
      </div>
    );
  } else {
    body = (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[56rem] border-collapse">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.name')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.condition')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.severity')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.channels')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('columns.enabled')}
              </th>
              <th scope="col" className="py-2 font-medium">
                {t('columns.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rules.data.rules.map((rule) => (
              <RuleRow
                key={rule.id}
                rule={rule}
                summary={summarize(rule.condition)}
                now={now()}
                onEdit={(r) => setEditing({ rule: r })}
              />
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-prose space-y-1">
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">{t('description')}</p>
        </div>
        {hasRules && newButton}
      </div>
      {body}
      {editing && <RuleDialog rule={editing.rule} onClose={() => setEditing(null)} />}
    </div>
  );
}
